import React, { useEffect, useRef, useState } from "react";
import { Image } from 'react-bootstrap';
import { IoIosSend } from "react-icons/io";
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import icon1 from "../../assets/img/chart/Frame 52 (1).png";
import icon2 from "../../assets/img/chart/Frame 52 (2).png";
import icon from "../../assets/img/chart/Frame 52.png";
import FileImage from "../../assets/img/fileImage.png";
import DigitalHeader from '../degitallibraryHeader';
import Footer from '../footer/footer';
import { FaRegStar, FaStar } from "react-icons/fa";

const MarkdownComponents = {
    code({ node, inline, className, children, ...props }) {
        const match = /language-(\w+)/.exec(className || '');
        return !inline && match ? (
            <SyntaxHighlighter
                style={vscDarkPlus}
                language={match[1]}
                PreTag="div"
                {...props}
            >
                {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
        ) : (
            <code className={className} {...props}>
                {children}
            </code>
        );
    },
    table({ children }) {
        return (
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    {children}
                </table>
            </div>
        );
    },
    th({ children }) {
        return (
            <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {children}
            </th>
        );
    },
    td({ children }) {
        return (
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {children}
            </td>
        );
    }
};

export const DigitalLibrary = () => {
    const chatMessagesEndRef = useRef(null);

    const [companyName, setCompanyName] = useState("netflix");
    const [permalink, setPermalink] = useState("");
    const [showIntroPanel, setShowIntroPanel] = useState(true);
    const [messages, setMessages] = useState([]);
    const [userInput, setUserInput] = useState("");
    const [hasAskedQuestion, setHasAskedQuestion] = useState(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const permalink = urlParams.get("query") || "netflix";
        setPermalink(permalink);
        setCompanyName(permalink);
    }, []);

    const handleUserInputChange = (e) => {
        setUserInput(e.target.value);
    };

    const handleSendMessage = async () => {
        if (userInput.trim() === "") return;
      
        // Add the user's message first
        const userMessage = { role: "user", text: userInput, id: Date.now(), loading: true };
        setMessages(prev => [...prev, userMessage]);
        setUserInput("");  // Clear input
        setHasAskedQuestion(true); // Set the state to indicate a question has been asked
    
        try {
            // Add a placeholder for the bot's message (it will be updated later)
            const botMessageId = Date.now() + 1; // Unique ID for bot message
            const newBotMessage = { role: "bot", text: "", id: botMessageId, loading: true };
            setMessages(prev => [...prev, newBotMessage]);
    
            // Fetch the bot's response
            const response = await fetch('https://knowledge-base.fly.dev/chat/stream', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    messages: [{ content: userInput, role: "user" }],
                    context: { overrides: { top: 3, temperature: 0.3, retrieval_mode: "hybrid", semantic_ranker: true } },
                    session_state: null
                })
            });
    
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let accumulatedText = "";
    
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
    
                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');
    
                for (const line of lines) {
                    if (line.trim()) {
                        try {
                            const parsed = JSON.parse(line);
                            if (parsed.delta?.content) {
                                accumulatedText += parsed.delta.content;
                                // Update the bot's message incrementally as content is received
                                setMessages(prev =>
                                    prev.map(msg =>
                                        msg.id === botMessageId
                                            ? { ...msg, text: accumulatedText, loading: false }
                                            : msg
                                    )
                                );
                            }
                        } catch (e) {
                            console.error('Error parsing JSON:', e);
                        }
                    }
                }
            }
    
        } catch (error) {
            console.error("Error in chat:", error);
            setMessages(prev => [...prev, { role: "bot", text: "Sorry, there was an error processing your request.", loading: false }]);
        }
    };
    
    useEffect(() => {
        // Scroll to bottom whenever messages change
        chatMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);
    

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <>
            <DigitalHeader />
            <div className="py-3">
                {!hasAskedQuestion && (
                    <div className="contatent-div">
                        <div className='text-center align-items-center'><Image src={FileImage} /></div>
                        <div className="digitallibrary-title-main">
                            <span className="digitallibrary-title">Explore Our Digital Library</span>
                            <p className="digitallibrary-p">Tap into a world of knowledge with our Digital Library chatbot. Ask any question to navigate through extensive data and insights, tailored specifically for your needs. Start your discovery journey now!</p>
                        </div>
                        {showIntroPanel && (
                            <div className="grid-container row">
                                <div className="card">
                                    <img className='icon-chtbort' src={icon} alt="Icon" />
                                    <p>How does Nvidia differentiate from AMD?</p>
                                </div>
                                <div className="card">
                                    <img className='icon-chtbort' src={icon1} alt="Icon" />
                                    <p>What is the difference between PPA and vPPA?</p>
                                </div>
                                <div className="card">
                                    <img className='icon-chtbort' src={icon2} alt="Icon" />
                                    <p>Tell me more about Anthropic AI</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div className={`chat-container ${hasAskedQuestion ? 'expanded' : ''}`}>
                    <div className="chat-messages">
                        {messages.map((message, index) => (
                            <div key={index} className={`chat-message ${message.role}`}>
                                {message.role === 'user' ? (
                                    <p>{message.text}</p>
                                ) : (
                                    <div className="markdown-content">
                                        <svg aria-hidden="true" aria-label="Answer logo" fill="rgba(115, 118, 225, 1)" class="___12fm75w f1w7gpdv fez10in fg4l7m0" role="img" width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg"><path d="M10.06 18.7a1.63 1.63 0 0 0 2.43-.67l.77-2.34a3.82 3.82 0 0 1 2.42-2.42l2.23-.73a1.61 1.61 0 0 0 1.07-1.3 1.62 1.62 0 0 0-1.13-1.78l-2.21-.72a3.82 3.82 0 0 1-2.42-2.42l-.73-2.23a1.62 1.62 0 0 0-3.07.01l-.74 2.27a3.82 3.82 0 0 1-2.36 2.36l-2.24.73a1.63 1.63 0 0 0 .02 3.07l2.22.72a3.86 3.86 0 0 1 2.42 2.43l.73 2.23c.1.32.32.6.6.8Zm9.74 6.08a1.2 1.2 0 0 0 1.84-.6l.37-1.14a1.6 1.6 0 0 1 1.02-1.02l1.16-.38a1.21 1.21 0 0 0 .59-1.83 1.23 1.23 0 0 0-.62-.45L23 18.99A1.6 1.6 0 0 1 22 17.97l-.38-1.16a1.2 1.2 0 0 0-2.27.01l-.38 1.15a1.6 1.6 0 0 1-.99 1.02l-1.17.38a1.21 1.21 0 0 0-.58 1.83c.15.2.36.36.6.44l1.14.37A1.6 1.6 0 0 1 19 23.04l.37 1.16c.09.24.24.44.44.58Z" fill="rgba(115, 118, 225, 1)"></path></svg> 
                                        {message.loading ? (
                                            <p className="loading-text">
                                                Generating answer<span className="dot">.</span><span className="dot">.</span><span className="dot">.</span>
                                            </p>
                                        ) : (
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={MarkdownComponents}
                                            >
                                                {message.text}
                                            </ReactMarkdown>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                        <div ref={chatMessagesEndRef}></div>
                    </div>
                    <div className="chat-input">
                        <textarea
                           
                            value={userInput}
                            rows={3}
                            onChange={handleUserInputChange}
                            onKeyPress={handleKeyPress}
                            placeholder="Type a new question (e.g.Type a new question (e.g. what is the difference between Quantexa and Sayari Labs?)"
                        />
                        <button onClick={handleSendMessage}>
                            <IoIosSend />
                        </button>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

